import React, { useState, useEffect } from 'react';
import { Card, Select, Button, Table, Statistic, Row, Col, Spin, message } from 'antd';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import apiClient from '../../../utils/apiClient';
import { downloadCSV } from '../../../utils/csvExport';

const { Option } = Select;

interface MemberAnalyticsData {
  rank_tier: string;
  gender: string;
  member_count: number;
  issued_count: number;
  used_count: number;
  usage_rate: number;
  expired_count: number;
  unused_count: number;
  avg_amount: number;
}

const MemberAnalytics: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<MemberAnalyticsData[]>([]);
  const [rankTier, setRankTier] = useState('all');
  const [gender, setGender] = useState('all');

  const fetchData = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (rankTier !== 'all') params.rank_tier = rankTier;
      if (gender !== 'all') params.gender = gender;

      const response = await apiClient.get('/coupon-analytics/analytics/by-member', { params });

      if (response.data.success) {
        setData(response.data.data);
      } else {
        message.error('データ取得に失敗しました');
      }
    } catch (error) {
      console.error('会員別分析エラー:', error);
      message.error('データ取得エラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleExportCSV = () => {
    const csvData = data.map(row => ({
      'ランクティア': row.rank_tier || '-',
      '性別': row.gender === 'male' ? '男性' : row.gender === 'female' ? '女性' : row.gender || '-',
      '会員数': row.member_count,
      '発行数': row.issued_count,
      '使用数': row.used_count,
      '使用率(%)': row.usage_rate?.toFixed(2) || '0',
      '期限切れ': row.expired_count,
      '未使用': row.unused_count,
      '平均利用金額': row.avg_amount ? `¥${Math.round(row.avg_amount).toLocaleString()}` : '-'
    }));

    downloadCSV(csvData, '会員別クーポン分析.csv');
    message.success('CSVファイルをダウンロードしました');
  };

  const columns = [
    {
      title: 'ランクティア',
      dataIndex: 'rank_tier',
      key: 'rank_tier',
      render: (text: string) => text || '-',
      width: 120
    },
    {
      title: '性別',
      dataIndex: 'gender',
      key: 'gender',
      render: (text: string) => {
        if (text === 'male') return '👨 男性';
        if (text === 'female') return '👩 女性';
        return text || '-';
      },
      width: 100
    },
    {
      title: '会員数',
      dataIndex: 'member_count',
      key: 'member_count',
      align: 'right' as const,
      width: 100
    },
    {
      title: '発行数',
      dataIndex: 'issued_count',
      key: 'issued_count',
      align: 'right' as const,
      width: 100
    },
    {
      title: '使用数',
      dataIndex: 'used_count',
      key: 'used_count',
      align: 'right' as const,
      width: 100
    },
    {
      title: '使用率',
      dataIndex: 'usage_rate',
      key: 'usage_rate',
      render: (rate: number) => {
        const color = rate >= 70 ? 'text-green-600' : rate >= 50 ? 'text-yellow-600' : 'text-red-600';
        return (
          <span className={`font-bold ${color}`}>
            {rate?.toFixed(1) || '0.0'}%
          </span>
        );
      },
      align: 'right' as const,
      width: 120
    },
    {
      title: '期限切れ',
      dataIndex: 'expired_count',
      key: 'expired_count',
      align: 'right' as const,
      width: 100
    },
    {
      title: '未使用',
      dataIndex: 'unused_count',
      key: 'unused_count',
      align: 'right' as const,
      width: 100
    },
    {
      title: '平均利用金額',
      dataIndex: 'avg_amount',
      key: 'avg_amount',
      render: (amount: number) => amount ? `¥${Math.round(amount).toLocaleString()}` : '-',
      align: 'right' as const,
      width: 140
    }
  ];

  // サマリー統計
  const totalIssued = data.reduce((sum, row) => sum + row.issued_count, 0);
  const totalUsed = data.reduce((sum, row) => sum + row.used_count, 0);
  const overallUsageRate = totalIssued > 0 ? (totalUsed / totalIssued * 100).toFixed(1) : '0.0';

  return (
    <div>
      {/* 検索フィルター */}
      <Card className="mb-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ランクティア
            </label>
            <Select
              value={rankTier}
              onChange={setRankTier}
              style={{ width: 150 }}
            >
              <Option value="all">全て</Option>
              <Option value="A">Aランク</Option>
              <Option value="B">Bランク</Option>
              <Option value="C">Cランク</Option>
              <Option value="D">Dランク</Option>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              性別
            </label>
            <Select
              value={gender}
              onChange={setGender}
              style={{ width: 150 }}
            >
              <Option value="all">全て</Option>
              <Option value="male">男性</Option>
              <Option value="female">女性</Option>
            </Select>
          </div>

          <div className="flex items-end gap-2">
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={fetchData}
              loading={loading}
            >
              検索
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExportCSV}
              disabled={data.length === 0}
            >
              CSV出力
            </Button>
          </div>
        </div>
      </Card>

      {/* サマリーカード */}
      <Row gutter={16} className="mb-4">
        <Col span={8}>
          <Card>
            <Statistic
              title="総発行数"
              value={totalIssued}
              suffix="枚"
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="総使用数"
              value={totalUsed}
              suffix="枚"
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="全体使用率"
              value={overallUsageRate}
              suffix="%"
              valueStyle={{
                color: parseFloat(overallUsageRate) >= 70 ? '#3f8600' :
                       parseFloat(overallUsageRate) >= 50 ? '#faad14' : '#cf1322'
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* データテーブル */}
      <Card>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={data}
            rowKey={(record) => `${record.rank_tier}-${record.gender}`}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `全 ${total} 件`
            }}
            locale={{
              emptyText: '該当するデータがありません'
            }}
          />
        </Spin>
      </Card>
    </div>
  );
};

export default MemberAnalytics;
